/**
 * 配送依頼 汎用CSVインポート（#4728）で、CSV の各列を紐づける先となる「ロジGoの配送依頼項目」の定義。
 *
 * 出典（正典）: 取込側のフィールドセット＝標準CSV取込が受け付ける列
 * （logi-go-api/src/shared/constants/shipment-order-csv-headers.constant.ts と、
 * 汎用取込の項目キー→標準ヘッダー対応表）。汎用取込は、マッピングで組み立てた行を
 * 標準取込と同じ検証・保存処理に通すため、ここに無い項目は取り込めない。
 * 作成フォームにしか無い項目（請求計上日・税区分・払い運賃・荷姿数）は標準取込も受け付けないので含めない。
 *
 * - required は確認画面で必須として扱う6項目（荷主・配送種別・発地・着地・配送日・着日）。
 * - 発地/着地の時間制限は開始・終了を別の項目にする（1 つの CSV 列に 2 つの値は入らない）。
 *
 * 表示ラベル・グループ名は constants/common/message.constants.ts の
 * LOGI_FIELD_LABELS / LOGI_FIELD_GROUP_LABELS に集約（日本語ハードコード回避）。
 */

import {
  LOGI_FIELD_GROUP_LABELS,
  LOGI_FIELD_LABELS
} from '@/constants/common/message.constants'

/** マッピング先の項目グループ ID（プルダウンの optgroup 表示順に使う） */
export const LOGI_FIELD_GROUPS = [
  'required',
  'shipping',
  'charge',
  'item',
  'customKey',
  'dispatched'
] as const

export type LogiFieldGroup = (typeof LOGI_FIELD_GROUPS)[number]

/** グループ ID → 表示名 */
export const LOGI_FIELD_GROUP_LABEL: Record<LogiFieldGroup, string> = {
  required: LOGI_FIELD_GROUP_LABELS.required,
  shipping: LOGI_FIELD_GROUP_LABELS.shipping,
  charge: LOGI_FIELD_GROUP_LABELS.charge,
  item: LOGI_FIELD_GROUP_LABELS.item,
  customKey: LOGI_FIELD_GROUP_LABELS.customKey,
  dispatched: LOGI_FIELD_GROUP_LABELS.dispatched
}

export type LogiFieldValue = keyof typeof LOGI_FIELD_LABELS

export type LogiField = {
  /** create-shipment-order-create-form-value.ts のスキーマキーに対応 */
  value: LogiFieldValue
  /** 表示ラベル（LOGI_FIELD_LABELS 由来） */
  label: string
  group: LogiFieldGroup
  required?: boolean
}

/** value と group・required のみを定義（label は LOGI_FIELD_LABELS から解決） */
const LOGI_FIELD_DEFS: ReadonlyArray<{
  value: LogiFieldValue
  group: LogiFieldGroup
  required?: boolean
}> = [
  // ── 必須項目（実スキーマで非nullable & ユーザー入力必須）
  { value: 'customerId', group: 'required', required: true },
  { value: 'shipmentCategoryId', group: 'required', required: true },
  { value: 'shippingDate', group: 'required', required: true },
  { value: 'deliveryDate', group: 'required', required: true },
  { value: 'originSiteId', group: 'required', required: true },
  { value: 'destSiteId', group: 'required', required: true },
  // ── 配送情報（任意）
  { value: 'orderNo', group: 'shipping' },
  { value: 'partnerId', group: 'shipping' },
  { value: 'packageCategoryId', group: 'shipping' },
  { value: 'truckModelId', group: 'shipping' },
  // 時間制限は開始/終了が別値（1CSV列に2値は入らない）のため、Start/Endを個別のマッピング先にする
  // （標準CSVパーサーの実ヘッダー4列・aspida生成型のフィールド名と一致させている）
  { value: 'departureTimeRestrictionStart', group: 'shipping' },
  { value: 'departureTimeRestrictionEnd', group: 'shipping' },
  { value: 'arrivalTimeRestrictionStart', group: 'shipping' },
  { value: 'arrivalTimeRestrictionEnd', group: 'shipping' },
  // shipmentOrderNote/carrierType: 標準CSVパーサーの実ヘッダー（配送依頼備考/配送条件、
  // shipment-order-csv-headers.constant.ts の ADD_INFO_HEADERS）に対応する項目。
  // フィールド名は Row型(shipment-order-file.service.ts) に合わせている
  { value: 'shipmentOrderNote', group: 'shipping' },
  { value: 'carrierType', group: 'shipping' },
  { value: 'note', group: 'shipping' },
  // ── 運賃・費用（任意）
  { value: 'income', group: 'charge' },
  // ── 明細項目（任意・items配下）
  { value: 'itemCode', group: 'item' },
  { value: 'itemName', group: 'item' },
  { value: 'packagedLoadQuantity', group: 'item' },
  { value: 'quantity', group: 'item' },
  { value: 'weight', group: 'item' },
  // ── 標準機能パリティ分（標準パーサーが受け付ける列は汎用でも全て扱えるようにする）
  //    出典: logi-go-api/src/shared/constants/shipment-order-csv-headers.constant.ts
  //    （取込側フィールドセット。作成フォーム由来の上記カタログに無い列を補完する）
  { value: 'externalKey', group: 'shipping' },
  { value: 'customerOrderNumber', group: 'shipping' },
  // 取消の行の印（標準 CSV の任意列「取消」と同じ。○ の行は明細 ID で登録済みの明細を外す）
  { value: 'revoke', group: 'shipping' },
  { value: 'supplement', group: 'shipping' },
  { value: 'customerName', group: 'shipping' },
  { value: 'partnerName', group: 'shipping' },
  // 発着地の名称・住所等（マスタに無い地点コードを取り込むときの地点自動作成に使われる）
  { value: 'originName', group: 'shipping' },
  { value: 'originContractAreaCode', group: 'shipping' },
  { value: 'destName', group: 'shipping' },
  { value: 'destContractAreaCode', group: 'shipping' },
  { value: 'destAddress', group: 'shipping' },
  { value: 'destNote', group: 'shipping' },
  // カスタムキー: 標準取込では DC のカスタムキーマスタのラベルとヘッダー突合で解決するが、
  // 汎用ではユーザーが明示的に紐づけるため、ただのマッピング先フィールドになる。
  // 選択肢ラベルは拠点のカスタムキー設定名を使う（buildCustomKeyFieldState）。
  { value: 'customKey1', group: 'customKey' },
  { value: 'customKey2', group: 'customKey' },
  { value: 'customKey3', group: 'customKey' },
  { value: 'customKey4', group: 'customKey' },
  { value: 'customKey5', group: 'customKey' },
  // 配車済み情報（標準取込の dispatched 列群。群の有無検知は不要——マッピング宣言が構成を決める）
  { value: 'dispatchSlotType', group: 'dispatched' },
  { value: 'dispatchSlotLabel', group: 'dispatched' },
  { value: 'dispatchKey', group: 'dispatched' },
  { value: 'dispatchDate', group: 'dispatched' },
  { value: 'dispatchSlotDepartureAt', group: 'dispatched' },
  { value: 'dispatchSlotArrivalAt', group: 'dispatched' },
  { value: 'hasDriverRestraint', group: 'dispatched' },
  { value: 'payment', group: 'dispatched' }
]

/**
 * 配送依頼項目（右プルダウンの選択肢）。label は LOGI_FIELD_LABELS から解決。
 */
export const LOGI_FIELDS: readonly LogiField[] = LOGI_FIELD_DEFS.map((def) => ({
  ...def,
  label: LOGI_FIELD_LABELS[def.value]
}))

/** 必須項目のみ */
export const REQUIRED_LOGI_FIELDS = LOGI_FIELDS.filter((f) => f.required)

/**
 * 拠点のカスタムキー設定を反映した、カスタムキー項目の表示名と使えない項目。
 * 取込は拠点で「表示ON・ラベルあり」のスロットしか受け付けない（BE の標準パーサーと同じ条件）ので、
 * それ以外のスロットはマッピング先に出さない。
 */
export type CustomKeyFieldState = {
  labels: Partial<Record<LogiFieldValue, string>>
  unavailable: ReadonlySet<LogiFieldValue>
}

export const EMPTY_CUSTOM_KEY_FIELD_STATE: CustomKeyFieldState = {
  labels: {},
  unavailable: new Set()
}

const CUSTOM_KEY_FIELDS: readonly LogiFieldValue[] = [
  'customKey1',
  'customKey2',
  'customKey3',
  'customKey4',
  'customKey5'
]

/**
 * 拠点のカスタムキー設定から CustomKeyFieldState を作る。
 * 設定がまだ取れていない（undefined）あいだは何も絞らない（読み込み中に選択肢が消えないように）。
 */
export const buildCustomKeyFieldState = (
  settings:
    | ReadonlyArray<{
        slotNumber: number
        label?: string | null
        isVisible: boolean
      }>
    | undefined
): CustomKeyFieldState => {
  if (!settings) return EMPTY_CUSTOM_KEY_FIELD_STATE
  const labels: Partial<Record<LogiFieldValue, string>> = {}
  const unavailable = new Set<LogiFieldValue>(CUSTOM_KEY_FIELDS)
  for (const s of settings) {
    const field = CUSTOM_KEY_FIELDS[s.slotNumber - 1]
    if (!field || !s.isVisible || !s.label) continue
    labels[field] = s.label
    unavailable.delete(field)
  }
  return { labels, unavailable }
}

/** 項目の表示名に拠点のカスタムキー名を反映する。 */
export const relabelField = (
  field: LogiField,
  state: CustomKeyFieldState
): LogiField => {
  const label = state.labels[field.value]
  return label ? { ...field, label } : field
}
